import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import PSAIModal from '@PSAIComponents/PSAIModal';
import MattingOptGlasses from '@view/compoents/MattingOpt/MattingOptGlasses';
import { IMAGE_TYPE } from '@utils/CONST';
import Utils from '@utils/utils';
import styles from './index.module.scss';

const createEmptyMasks = async (imageUrl) => {
    const imageBase64 = await Utils.urlToBase64(imageUrl);
    const { width, height } = await Utils.getImageDimensions(imageBase64);
    const binaryCanvas = document.createElement('canvas');
    const binaryContext = binaryCanvas.getContext('2d');
    binaryCanvas.width = width;
    binaryCanvas.height = height;
    binaryContext.fillStyle = '#000000';
    binaryContext.fillRect(0, 0, width, height);

    const blueCanvas = document.createElement('canvas');
    blueCanvas.width = width;
    blueCanvas.height = height;

    return {
        binaryMask: binaryCanvas.toDataURL('image/png'),
        blueMask: blueCanvas.toDataURL('image/png'),
    };
};

const RepaintMaskModal = ({ open, imageUrl, maskUrl, onCancel, onSave }) => {
    const mattingRef = useRef(null);
    const [initialMasks, setInitialMasks] = useState(null);
    const [initializing, setInitializing] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let active = true;

        if (!open || !imageUrl) {
            setInitialMasks(null);
            return () => {
                active = false;
            };
        }

        setInitialMasks(null);
        setInitializing(true);
        Promise.resolve(maskUrl
            ? Utils.convertWhiteImageToBlue(maskUrl).then((blueMask) => ({
                binaryMask: maskUrl,
                blueMask,
            }))
            : createEmptyMasks(imageUrl))
            .then((masks) => {
                if (active) {
                    setInitialMasks({
                        ...masks,
                        imageUrl,
                    });
                }
            })
            .catch(() => {
                if (active) {
                    message.error('涂抹区域初始化失败，请重试');
                    onCancel?.();
                }
            })
            .finally(() => {
                if (active) setInitializing(false);
            });

        return () => {
            active = false;
        };
    }, [imageUrl, maskUrl, onCancel, open]);

    const modalReady = open && initialMasks?.imageUrl === imageUrl;

    const handleSave = useCallback(async () => {
        if (!mattingRef.current || initializing) return;

        setSaving(true);
        try {
            const result = await mattingRef.current.saveHandle();
            if (!result?.whiteMask) {
                message.error('请先在原图上标记出要配色的区域！', 5);
                return;
            }
            await onSave?.(result.whiteMask);
        } catch (error) {
            message.error(error?.message || '保存涂抹区域失败，请重试');
        } finally {
            setSaving(false);
        }
    }, [initializing, onSave]);

    return (
        <PSAIModal
            title="自定义重绘区域"
            maxWidth={1380}
            getContainer="body"
            zIndex={10000}
            closable={false}
            maskClosable={false}
            open={modalReady}
            confirmLoading={saving || initializing}
            okText="保存修改"
            cancelText="放弃修改"
            onCancel={onCancel}
            onOk={handleSave}
        >
            <div className={styles.content}>
                {initialMasks ? (
                    <MattingOptGlasses
                        key={`${imageUrl}-${maskUrl || 'empty'}`}
                        ref={mattingRef}
                        imageType={IMAGE_TYPE.GLASSES_TRYON}
                        blueMask={initialMasks.blueMask}
                        whiteMask={initialMasks.binaryMask}
                        inputImage={imageUrl}
                    />
                ) : null}
            </div>
        </PSAIModal>
    );
};

export default RepaintMaskModal;
